import { userInfoAtom, userLoggedInAtom } from '@/jotai/atoms/userAtom';
import { CloseOutlined, MenuOutlined } from '@ant-design/icons';
import { Button, Layout, Typography } from 'antd';
import { useSetAtom } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import './AppLayout.css';
import { HeaderActions } from './components/HeaderActions';
import { SiderMenu } from './components/SiderMenu';
import { AnimatedBackground } from './components/AnimatedBackground';

const { Header, Content, Footer, Sider } = Layout;
const { Text } = Typography;

const MOBILE_BREAKPOINT = 768;
const COLLAPSED_WIDTH = 50;
const EXPANDED_WIDTH = 200;

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  title = 'Dashboard',
}) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const { isMobile } = useResponsive(MOBILE_BREAKPOINT);

  const isLoggedIn = localStorage.getItem('userLoggedIn') === 'true';
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

  const setUserLoggedIn = useSetAtom(userLoggedInAtom);
  const setUserInfo = useSetAtom(userInfoAtom);

  useEffect(() => {
    setCollapsed(isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login', { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const handleLogout = () => {
    setUserInfo(null);
    setUserLoggedIn(false);
    localStorage.removeItem('userInfo');
    localStorage.removeItem('userLoggedIn');
    navigate('/login', { replace: true });
  };

  const logoText = useMemo(() => (collapsed ? 'S' : 'Supp Admin'), [collapsed]);

  const handleMenuClick = () => {
    if (isMobile) {
      setCollapsed(true);
    }
  };

  return (
    <Layout className={`custom-layout ${isMobile ? 'mobile-layout' : ''}`}>
      <Sider
        width={EXPANDED_WIDTH}
        theme="light"
        collapsedWidth={isMobile ? 0 : COLLAPSED_WIDTH}
        className={`custom-sider ${isMobile ? 'mobile-sider' : ''}`}
        collapsed={collapsed}
        onCollapse={setCollapsed}
        collapsible={!isMobile}
        trigger={null}
      >
        <AnimatedBackground />
        {!isMobile && <div className="logo">{logoText}</div>}
        <SiderMenu collapsed={collapsed} onMenuClick={handleMenuClick} />
      </Sider>

      <Layout className='custom-main'>
        <Header className={`custom-header ${isMobile ? 'mobile-header' : ''}`}>
          <AnimatedBackground />
          {isMobile && (
            <Button
              type="text"
              icon={collapsed ? <MenuOutlined /> : <CloseOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="mobile-menu-trigger"
            />
          )}
          <div className="header-content">
            <Text strong className="header-title">
              {title}
            </Text>
            <HeaderActions
              userInfo={userInfo}
              isMobile={isMobile}
              onLogout={handleLogout}
            />
          </div>
        </Header>

        <Content
          className={`custom-content ${isMobile ? 'mobile-content' : ''}`}
        >
          {children}
        </Content>

        <Footer className={`custom-footer ${isMobile ? 'mobile-footer' : ''}`}>
          Supp Admin web {new Date().getFullYear()}
        </Footer>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
